import React, { useMemo } from "react";
import { Box, MenuItem, Select, Switch, FormControlLabel, Typography, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { observer } from "mobx-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";

import GenericInfiniteTable, {
  ColumnDef,
} from "../../../molecules/PatientRxTables/GenericInfiniteTable";
import { PatientTableDto } from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { RegistryContext } from "../../../stores/Registry/registry.provider";
import { registryKeys } from "../../../stores/Registry/registry.keys";
import { useApi } from "../../../api/ApiProvider";
import { useLocationId } from "../../../stores/User/User.hooks";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { useNavigate } from "react-router-dom";
import { useTarifGroups } from "../../../stores/MasterTables/therapies.queries";
import FlatCard from "../../../atoms/FlatCard";

/**
 * Computes a patient's age in completed years from their DOB.
 */
const calculateAge = (dob?: string | null): string => {
  if (!dob) return "-";
  const m = moment(dob);
  if (!m.isValid()) return "-";
  return moment().diff(m, "years").toString();
};

/**
 * Renders the patient's primary address (or the first available one).
 * Address details are shown inside a tooltip for visibility.
 */
const renderAddress = (patient: PatientTableDto): React.ReactNode => {
  const addresses = patient.addresses || [];
  const primary = addresses.find((a) => (a as any).isPrimary) || addresses[0];
  if (!primary) return "-";
  const { addressLine1, addressLine2, city, zip } = (primary as any).contactInfo ?? {};
  const display = `${addressLine1 ?? ""} ${zip ?? ""} ${city ?? ""}`.trim() || "-";
  return (
    <Tooltip
      title={
        <Typography sx={{ whiteSpace: "pre-line" }}>
          {`${addressLine1 ?? "-"}\n${addressLine2 ?? "-"}\n${zip ?? "-"} ${city ?? "-"}`}
        </Typography>
      }
      arrow
    >
      <span>{display}</span>
    </Tooltip>
  );
};

/**
 * Returns a short, human-friendly Zuzahlung label.
 *
 * Distinguishes between:
 *   - "Zuzahlungspflichtig"   – patient must co-pay
 *   - "Befreit"               – patient is exempt and the proving document is present
 *   - "Befreit – Beleg fehlt" – patient is marked exempt but the document is missing
 *
 * The PatientTableDto today exposes only `coPayment` (boolean), so we render based
 * on what's available; once the backend DTO is extended with copayment-exemption
 * info, this helper will pick up the richer signal automatically.
 */
const renderCopayment = (p: PatientTableDto): React.ReactNode => {
  const exemptions = (p as any).copaymentExemptions as
    | Array<{ document?: unknown | null; endDate?: string }>
    | undefined;

  if (exemptions && exemptions.length > 0) {
    const now = new Date();
    const active = exemptions.find(
      (e) => !e.endDate || new Date(e.endDate) >= now,
    );
    if (active) {
      return active.document ? "Befreit" : "Befreit – Beleg fehlt";
    }
  }
  return p.coPayment ? "Zuzahlungspflichtig" : "Befreit";
};

const RegistryPatientsTab: React.FC = observer(() => {
  const RegistryStore = useStore(RegistryContext);
  const PatientsStore = useStore(PatientsContext);
  const {
    patientFilters: filters,
    setPatientFilters,
    patientSelectedIndex: selectedIndex,
    setPatientSelectedIndex: setSelectedIndex,
  } = RegistryStore;

  const locationId = useLocationId();
  const navigate = useNavigate();
  const { patients: patientsApi } = useApi();
  const { data: tarifGroups } = useTarifGroups();

  const tarifGroupOptions = useMemo<Array<{ id: string; name: string }>>(
    () =>
      (tarifGroups || []).map((tg: any) => ({
        id: tg.id,
        name: tg.name ?? tg.id,
      })),
    [tarifGroups],
  );

  // ---- Infinite query ------------------------------------------------------
  const effectiveFilters = useMemo(
    () => ({ ...filters, location: filters.location ?? locationId }),
    [filters, locationId],
  );

  const {
    data,
    isFetchingNextPage,
    isLoading,
    hasNextPage: hasMoreData,
    fetchNextPage,
  } = useInfiniteQuery<PatientTableDto[], Error>({
    queryKey: registryKeys.patients.filtered(effectiveFilters),
    queryFn: async ({ pageParam = 1 }) =>
      patientsApi.getFilteredAllPatients({
        ...(effectiveFilters as any),
        page: pageParam as number,
        limit: effectiveFilters.limit,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length >= (effectiveFilters.limit ?? 50) ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: moment.duration(10, "minutes").asMilliseconds(),
    enabled: !!locationId,
    meta: { errorMessage: "Patientenliste konnte nicht geladen werden" },
  });

  const patients: PatientTableDto[] = data?.pages.flat() ?? [];

  // ---- Open patient on row click ------------------------------------------
  const handleRowClick = (p: PatientTableDto) => {
    PatientsStore.setSelectedPatientId(p.id);
    navigate("/patient");
  };

  // ---- Columns -------------------------------------------------------------
  const columns: ColumnDef<PatientTableDto>[] = useMemo(
    () => [
      {
        key: "id",
        header: "ID",
        align: "left",
        render: (p) => p.id,
        width: "80px",
        sortable: true,
      },
      {
        key: "patientName",
        header: "Name (Nachname, Vorname)",
        render: (p) => `${p.lastName ?? "-"}, ${p.firstName ?? "-"}`,
        sortable: true,
      },
      {
        key: "dob",
        header: "Geburtsdatum",
        render: (p) => (p.dob ? format(new Date(p.dob), "dd.MM.yyyy") : "-"),
        sortable: true,
      },
      {
        key: "age",
        header: "Alter",
        render: (p) => calculateAge(p.dob),
      },
      {
        key: "payer",
        header: "Versicherung",
        render: (p) => p.payer?.name ?? "-",
      },
      {
        key: "copayment",
        header: "Zuzahlung",
        render: renderCopayment,
      },
      {
        key: "address",
        header: "Adresse",
        render: renderAddress,
      },
    ],
    [],
  );

  // ---- Sort change --------------------------------------------------------
  const handleSortChange = (columnKey: string) => {
    setPatientFilters((prev) => {
      const currentSort = (prev.sortBy as Record<string, "ASC" | "DESC"> | undefined) || {};
      const currentDir = currentSort[columnKey];
      const newDir =
        currentDir === "ASC" ? "DESC" : currentDir === "DESC" ? undefined : "ASC";
      const next = { ...currentSort };
      if (!newDir) delete next[columnKey];
      else next[columnKey] = newDir;
      return { sortBy: next as any };
    });
  };

  // ---- Toolbar -------------------------------------------------------------
  const toolbar = (
    <FlatCard
      title="Patienten"
      sx={{ mb: 1 }}
      sxContent={{ p: 1.5 }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">Tarifgruppe</Typography>
          <Select
            size="small"
            value={filters.tarifGroupId ?? ""}
            onChange={(e) =>
              setPatientFilters({ tarifGroupId: (e.target.value as string) || undefined })
            }
            displayEmpty
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">
              <em>Alle</em>
            </MenuItem>
            {tarifGroupOptions.map((tg) => (
              <MenuItem key={tg.id} value={tg.id}>
                {tg.name}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">Zuzahlung</Typography>
          <Select
            size="small"
            value={filters.copayment ?? "any"}
            onChange={(e) =>
              setPatientFilters({ copayment: e.target.value as any })
            }
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="any">Alle</MenuItem>
            <MenuItem value="required">Zuzahlungspflichtig</MenuItem>
            <MenuItem value="exempted">Befreit (mit Beleg)</MenuItem>
            <MenuItem value="missingDoc">Befreit – Beleg fehlt</MenuItem>
          </Select>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={!!filters.includeDeleted}
              onChange={(e) => setPatientFilters({ includeDeleted: e.target.checked })}
            />
          }
          label="Gelöschte anzeigen"
        />
      </Box>
    </FlatCard>
  );

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <GenericInfiniteTable<PatientTableDto>
        data={patients}
        columns={columns}
        hasMoreData={!!hasMoreData}
        fetchMoreData={fetchNextPage}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        onRowClick={handleRowClick}
        scrollId="registryPatientsScroll"
        toolbar={toolbar}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        onSortChange={handleSortChange}
        sortBy={filters.sortBy as any}
        emptyMessage="Keine Patienten gefunden"
      />
    </Box>
  );
});

export default RegistryPatientsTab;
